import { Stack } from '@mui/material';
import { getMedicalReferrals, IMedicalReferral } from 'API/medicalReferrals';
import { CustomMetaTitle } from 'Components';
import { LinkReturnToMain } from 'Components/LinkReturnToMain';
import { AdvancedSearchContextProvider } from 'Context/MedicalReferralsContext/AdvancedSearchProvider';
import { Dictionary } from 'Dictionary';
import { DICTIONARY } from 'Enum';
import { useAppSelector, useDictionary, useIsDesktop } from 'Hooks';
import { useCallback, useEffect, useReducer, useState } from 'react';
import {
  IReferralsState,
  ReferralReducer,
} from 'reducer/MedicalReferrals/ReferralReducer';
import { ReferralsList } from './ReferralsList';
import { SearchFilter } from './Search';
import { Title } from './Title';
import { isAfter } from 'date-fns';
import { useLoader } from 'Hooks/useLoader';
import { NoMedicalReferralsFound } from './NoMedicalReferralsFound';

export const maxMedicalReferralsPerPage = 20;

export const MedicalReferrals = () => {
  const [textSearchValue, setTextSearchValue] = useState<string>('');
  const imposterId = useAppSelector(
    ({ imposter: { imposterId } }) => imposterId
  );

  const dictionary = useDictionary(Dictionary, DICTIONARY.MedicalReferrals);
  const isDesktop = useIsDesktop();

  const [referrals, dispatchReferrals]: [IReferralsState, Function] =
    useReducer(ReferralReducer, {
      validReferrals: [],
      otherReferrals: [],
      filteredReferrals: [],
    });

  const [providersList, setProviderList] = useState<string[]>([]);
  const [dateMinMaxRange, setdateMinMaxRange] = useState<{
    min: Date;
    max: Date;
  }>({ min: new Date(), max: new Date() });
  const [advancedSearchOpen, setAdvancedSearchOpen] = useState<boolean>(false);
  const { isLoading, setIsLoading, loader } = useLoader();

  const initReferrals = useCallback(
    async ({
      isFirstLoad = true,
      shouldOpenSearch = false,
    }: {
      isFirstLoad?: boolean;
      shouldOpenSearch?: boolean;
    }) => {
      setIsLoading(true);
      const result = await getMedicalReferrals(
        isFirstLoad ? maxMedicalReferralsPerPage : undefined
      );
      if (result) {
        dispatchReferrals({
          type: 'INITIALIZATION',
          payload: { value: result },
        });
        const supplierNames: string[] = result
          .map((referral: IMedicalReferral) => referral.supplierName)
          .filter(
            (value, index, self) => value && self.indexOf(value) === index
          );
        setProviderList(supplierNames);
        const minMaxDate = {
          min: result[0]?.openDate ? new Date(result[0].openDate) : new Date(),
          max: result[0]?.openDate ? new Date(result[0].openDate) : new Date(),
        };
        for (const { openDate } of result) {
          if (!openDate) continue;
          const open = new Date(openDate);
          if (isAfter(minMaxDate.min, open)) minMaxDate.min = new Date(open);
          if (isAfter(open, minMaxDate.max)) minMaxDate.max = new Date(open);
        }
        setdateMinMaxRange(minMaxDate);
      }
      setIsLoading(false);
      if (shouldOpenSearch) setAdvancedSearchOpen(true);
    },
    [setIsLoading]
  );

  useEffect(() => {
    initReferrals({});
  }, [initReferrals, imposterId]);

  if (isLoading) return loader;

  if (
    referrals.validReferrals.length === 0 &&
    referrals.otherReferrals.length === 0
  )
    return (
      <Stack gap={isDesktop ? '56px' : '32px'}>
        <CustomMetaTitle title={dictionary.title} />
        <LinkReturnToMain />
        <NoMedicalReferralsFound />
      </Stack>
    );

  return (
    <Stack gap={isDesktop ? '54px' : '24px'}>
      <CustomMetaTitle title={dictionary.title} />
      <LinkReturnToMain />
      <AdvancedSearchContextProvider>
        <Stack maxWidth='1029px' gap={isDesktop ? '48px' : '40px'}>
          <Stack gap={isDesktop ? '32px' : '24px'}>
            <Title />
            <SearchFilter
              setTextSearchValue={setTextSearchValue}
              dispatchReferrals={dispatchReferrals}
              providersList={providersList}
              referrals={referrals}
              dateMinMaxRange={dateMinMaxRange}
              loadAllReferrals={() =>
                initReferrals({
                  isFirstLoad: false,
                  shouldOpenSearch: true,
                })
              }
              referralsCount={
                referrals.validReferrals.length +
                referrals.otherReferrals.length
              }
              advancedSearchOpen={advancedSearchOpen}
              setAdvancedSearchOpen={setAdvancedSearchOpen}
            />
          </Stack>
          <ReferralsList
            referrals={referrals}
            dispatchReferrals={dispatchReferrals}
            textSearchValue={textSearchValue}
            loadAllReferrals={() => initReferrals({ isFirstLoad: false })}
            referralsCount={
              referrals.validReferrals.length + referrals.otherReferrals.length
            }
          />
        </Stack>
      </AdvancedSearchContextProvider>
    </Stack>
  );
};
